import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService, type FlagConfiguration } from '@atlassian/jira-flags';
import { MutationError } from '@atlassian/jira-mutation-error/src/index.tsx';
import type {
	renameNavigationItemMutation,
	renameNavigationItemMutation$variables,
} from '@atlassian/jira-relay/src/__generated__/renameNavigationItemMutation.graphql';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

type RenameNavigationItemArgs = {
	id: renameNavigationItemMutation$variables['input']['id'];
	label: renameNavigationItemMutation$variables['input']['label'];
	scopeId: renameNavigationItemMutation$variables['input']['scopeId'];
	typeKey: string;
	// the __typename is needed for the optimistic response
	__typename: string;
};

const DEFAULT_ERROR_FLAG: FlagConfiguration = {
	type: 'error',
	title: messages.defaultErrorFlagTitle,
	description: messages.defaultErrorFlagDescription,
	messageType: 'transactional',
	messageId:
		'horizontal-nav-tab-menu.services.rename-navigation-item.default-error-flag.error.horizontal-nav-tab-menu-rename-navigation-item-default-error',
};

const getFlagDetails = (error: MutationError): FlagConfiguration | null => {
	const { message } = error;
	const { statusCode } = error.extensions || {};

	switch (statusCode) {
		case 400:
		case 404:
			return {
				type: 'error',
				title: messages.defaultErrorFlagTitle,
				description: message,
				messageType: 'transactional',
				messageId:
					'horizontal-nav-tab-menu.services.rename-navigation-item.flag.error.horizontal-nav-tab-menu-rename-navigation-item-request-error',
			};
		case 401:
			return {
				type: 'error',
				title: messages.userNotLoggedInErrorFlagTitle,
				description: messages.userNotLoggedInErrorFlagDescription,
				messageType: 'transactional',
				messageId:
					'horizontal-nav-tab-menu.services.rename-navigation-item.flag.error.horizontal-nav-tab-menu-rename-navigation-item-not-logged-in-error',
			};
		case 403:
			return {
				type: 'error',
				title: messages.userNotAdminErrorFlagTitle,
				description: messages.userNotAdminErrorFlagDescription,
				messageType: 'transactional',
				messageId:
					'horizontal-nav-tab-menu.services.rename-navigation-item.flag.error.horizontal-nav-tab-menu-rename-navigation-item-not-admin-error',
			};
		default:
			return null;
	}
};

export const useRenameNavigationItem = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();
	const [commitMutation] = useMutation<renameNavigationItemMutation>(graphql`
		mutation renameNavigationItemMutation($input: JiraRenameNavigationItemInput!) {
			jira {
				renameNavigationItem(input: $input) {
					success
					errors {
						message
						extensions {
							statusCode
						}
					}
					navigationItem {
						label
					}
				}
			}
		}
	`);

	const onError = useCallback(
		(error: Error | MutationError) => {
			showFlag(DEFAULT_ERROR_FLAG);

			fireErrorAnalytics({
				meta: {
					id: 'renameNavigationItem',
					packageName: 'jiraHorizontalNavTabMenu',
					teamName: 'wanjel',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		[showFlag],
	);

	return useCallback(
		({ id, label, scopeId, typeKey, __typename }: RenameNavigationItemArgs) => {
			commitMutation({
				variables: {
					input: { id, label, scopeId },
				},
				optimisticResponse: {
					jira: {
						renameNavigationItem: {
							success: true,
							errors: null,
							navigationItem: {
								__typename,
								label,
								id,
							},
						},
					},
				},
				onError,
				onCompleted: (response) => {
					if (response?.jira?.renameNavigationItem?.success === true) {
						fireTrackAnalytics(createAnalyticsEvent({}), 'navigationItem renamed', { typeKey });
					} else {
						const firstError =
							response?.jira?.renameNavigationItem?.errors?.[0] ?? new Error('Unknown error');
						const error = new MutationError(firstError);
						const flagDetails = getFlagDetails(error);

						if (flagDetails) {
							showFlag(flagDetails);
							return;
						}

						onError(error);
					}
				},
			});
		},
		[commitMutation, createAnalyticsEvent, onError, showFlag],
	);
};
